import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import Avatar from "../components/Avatar";
import AvatarPicker from "../components/AvatarPicker";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Plus, Users, Trash2, Phone, Building2, X, KeyRound, FolderOpen, Download, FileText, Image as ImageIcon, Briefcase, UserCheck, Search, Check, XCircle, BadgeCheck, Eye, EyeOff, RefreshCw } from "lucide-react";

const EMPTY = { name: "", lastname: "", email: "", job: "", department: "", fiscal_code: "", phone: "", city: "", address: "", zip_code: "", birth: "", order_ids: [] };

export default function EmployeesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [companies, setCompanies] = useState([]);
  const [orders, setOrders] = useState([]);
  const [linkFor, setLinkFor] = useState(null); // employee selected for multi-company management
  const [addCid, setAddCid] = useState("");
  const [empFilter, setEmpFilter] = useState("all"); // all | supervisor | employee
  const filteredItems = useMemo(() => {
    if (empFilter === "supervisor") return items.filter(i => i.is_supervisor);
    if (empFilter === "employee") return items.filter(i => !i.is_supervisor);
    return items;
  }, [items, empFilter]);
  // Edit commesse rapido
  const [ordersFor, setOrdersFor] = useState(null);
  const [ordersDraft, setOrdersDraft] = useState([]);
  // Richieste pending dipendenti
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pending, setPending] = useState([]);
  const [pendingQuery, setPendingQuery] = useState("");
  const [pendingLoading, setPendingLoading] = useState(false);
  // Avatar picker per dipendente
  const [avatarFor, setAvatarFor] = useState(null);
  // Account portale dipendente
  const [accountFor, setAccountFor] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newPwd, setNewPwd] = useState("");
  // File dipendente (admin)
  const [filesFor, setFilesFor] = useState(null);
  const [filesList, setFilesList] = useState([]);

  const load = async () => setItems((await api.get("/employees")).data);
  const loadCompanies = async () => {
    // Superadmin vede tutte le aziende; admin normale ha solo la propria (nessun endpoint dedicato, quindi skip)
    if (user?.role === "superadmin") {
      try { setCompanies((await api.get("/admin/companies")).data); } catch (_) { /* noop */ }
    }
  };
  const loadOrders = async () => {
    try { setOrders((await api.get("/orders")).data); } catch (_) { /* noop */ }
  };
  const loadPending = async () => {
    setPendingLoading(true);
    try { setPending((await api.get("/employees/pending-requests")).data); }
    catch (_) { setPending([]); }
    finally { setPendingLoading(false); }
  };
  useEffect(() => { load(); loadCompanies(); loadOrders(); loadPending(); }, []); // eslint-disable-line

  const approvePending = async (rid) => {
    try {
      await api.post(`/employees/pending-requests/${rid}/approve`);
      toast.success("Dipendente approvato e collegato");
      await loadPending();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const rejectPending = async (rid) => {
    const note = window.prompt("Motivo del rifiuto (visibile internamente):", "");
    if (note === null) return;
    try {
      await api.post(`/employees/pending-requests/${rid}/reject`, { note });
      toast.success("Richiesta rifiutata");
      await loadPending();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const saveAvatarFor = async (emp, newAvatar) => {
    try {
      // Patch employee con avatar (richiede payload completo per il modello Pydantic)
      const payload = {
        name: emp.name, lastname: emp.lastname, email: emp.email,
        job: emp.job || "", department: emp.department || "",
        city: emp.city || "", address: emp.address || "",
        zip_code: emp.zip_code || "", phone: emp.phone || "",
        birth: emp.birth || null, fiscal_code: emp.fiscal_code,
        order_id: emp.order_id || null, order_ids: emp.order_ids || [],
        avatar: newAvatar,
      };
      await api.patch(`/employees/${emp.id}`, payload);
      toast.success("Avatar aggiornato");
      setItems((prev) => prev.map((p) => p.id === emp.id ? { ...p, avatar: newAvatar } : p));
      setAvatarFor(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const up = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleFormOrder = (oid) => {
    const arr = form.order_ids || [];
    setForm({ ...form, order_ids: arr.includes(oid) ? arr.filter((x) => x !== oid) : [...arr, oid] });
  };

  const save = async () => {
    try {
      await api.post("/employees", form);
      toast.success("Dipendente aggiunto");
      setForm(EMPTY); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // ---- Gestione commesse del dipendente (modifica rapida) ----
  const openOrders = (emp) => {
    setOrdersFor(emp);
    setOrdersDraft(emp.order_ids || (emp.order_id ? [emp.order_id] : []));
  };
  const toggleDraftOrder = (oid) => {
    setOrdersDraft(ordersDraft.includes(oid) ? ordersDraft.filter((x) => x !== oid) : [...ordersDraft, oid]);
  };
  const saveOrders = async () => {
    if (!ordersFor) return;
    try {
      const payload = {
        name: ordersFor.name, lastname: ordersFor.lastname, email: ordersFor.email,
        job: ordersFor.job || "", department: ordersFor.department || "",
        city: ordersFor.city || "", address: ordersFor.address || "",
        zip_code: ordersFor.zip_code || "", phone: ordersFor.phone || "",
        birth: ordersFor.birth || null,
        fiscal_code: ordersFor.fiscal_code,
        order_id: ordersDraft[0] || null,
        order_ids: ordersDraft,
      };
      await api.patch(`/employees/${ordersFor.id}`, payload);
      toast.success("Commesse aggiornate");
      setOrdersFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare il dipendente?")) return;
    await api.delete(`/employees/${id}`);
    toast.success("Eliminato"); load();
  };

  const toggleEmployeeActive = async (emp) => {
    const newActive = emp.enabled === false; // se ora è disabilitato → attiva
    let reason = "";
    if (!newActive) {
      reason = window.prompt(
        `Disattivare temporaneamente ${emp.name} ${emp.lastname}?\n\nIndica un motivo (es. Maternità, Aspettativa, Congedo, Sospensione):`,
        ""
      );
      if (reason === null) return; // cancel
    } else {
      if (!window.confirm(`Riattivare ${emp.name} ${emp.lastname}? Potrà di nuovo accedere al portale.`)) return;
    }
    try {
      await api.patch(`/employees/${emp.id}/toggle-active?active=${newActive}${reason ? `&reason=${encodeURIComponent(reason)}` : ""}`);
      toast.success(newActive ? "Dipendente riattivato" : "Dipendente disattivato");
      setItems((prev) => prev.map((p) => p.id === emp.id ? { ...p, enabled: newActive, disabled_reason: newActive ? "" : reason } : p));
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const toggleSupervisor = async (emp) => {
    if (!emp.email) {
      toast.error("Imposta prima un'email per il dipendente");
      return;
    }
    const isSup = !!emp.is_supervisor;
    const fullName = `${emp.name || ""} ${emp.lastname || ""}`.trim();
    const ok = isSup
      ? window.confirm(`Rimuovere il ruolo Supervisore da ${fullName}?\n\nTornerà a dipendente normale.`)
      : window.confirm(`Rendere ${fullName} SUPERVISORE?\n\nVedrà tutte le funzionalità del sub-amministratore in SOLA LETTURA (non potrà modificare nulla).`);
    if (!ok) return;
    try {
      const url = `/admin/employees/${emp.id}/${isSup ? "demote-supervisor" : "promote-supervisor"}`;
      await api.post(url, {});
      toast.success(isSup ? `${fullName} torna dipendente` : `${fullName} è ora Supervisore`);
      setItems((prev) => prev.map((p) => p.id === emp.id ? { ...p, is_supervisor: !isSup } : p));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const resetEmployeePwd = async (emp) => {
    if (!emp.email) {
      toast.error("Il dipendente non ha un'email collegata");
      return;
    }
    const newPwd = window.prompt(`Reimposta la password per ${emp.name} ${emp.lastname} (${emp.email}):`, "");
    if (!newPwd) return;
    if (newPwd.length < 4) { toast.error("Password troppo corta (min 4 caratteri)"); return; }
    try {
      await api.post("/admin/users/reset-password", { email: emp.email, new_password: newPwd });
      toast.success("Password reimpostata");
      window.alert(`✅ Password aggiornata.\n\nComunica al dipendente:\n• Email: ${emp.email}\n• Nuova password: ${newPwd}\n\nPotrà accedere al portale e cambiarla dopo il login.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore reset password");
    }
  };

  const addCompanyLink = async () => {
    if (!linkFor || !addCid) return;
    try {
      const r = await api.post(`/employees/${linkFor.id}/companies`, { company_id: addCid });
      toast.success("Azienda associata");
      setLinkFor(r.data);
      setAddCid("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const removeCompanyLink = async (cid) => {
    if (!linkFor) return;
    try {
      const r = await api.delete(`/employees/${linkFor.id}/companies/${cid}`);
      toast.success("Associazione rimossa");
      setLinkFor(r.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // ---- Gestione account portale ----
  const openAccount = async (emp) => {
    setAccountFor(emp);
    setNewEmail(emp.email || "");
    setNewPwd("");
    try {
      const r = await api.get(`/employees/${emp.id}/account`);
      setAccountInfo(r.data);
      if (r.data.email) setNewEmail(r.data.email);
    } catch { setAccountInfo({ exists: false }); }
  };
  const saveAccount = async () => {
    if (!accountFor) return;
    if (!newEmail || newPwd.length < 6) { toast.error("Email valida e password di almeno 6 caratteri"); return; }
    try {
      await api.post(`/employees/${accountFor.id}/account`, { email: newEmail, password: newPwd });
      toast.success(accountInfo?.exists ? "Password aggiornata" : "Account creato");
      setAccountFor(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const revokeAccount = async () => {
    if (!accountFor) return;
    if (!window.confirm("Revocare l'accesso al portale per questo dipendente?")) return;
    try {
      await api.delete(`/employees/${accountFor.id}/account`);
      toast.success("Accesso revocato");
      setAccountFor(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // ---- File dipendente (admin) ----
  const openFiles = async (emp) => {
    setFilesFor(emp);
    try { setFilesList((await api.get(`/employees/${emp.id}/files`)).data); }
    catch { setFilesList([]); }
  };
  const downloadEmpFile = async (f) => {
    try {
      const r = await api.get(`/employees/${filesFor.id}/files/${f.id}/download`);
      const a = document.createElement("a");
      a.href = r.data.content_base64;
      a.download = r.data.filename;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Errore download"); }
  };
  const delEmpFile = async (f) => {
    if (!window.confirm(`Eliminare "${f.filename}"?`)) return;
    await api.delete(`/employees/${filesFor.id}/files/${f.id}`);
    toast.success("Eliminato");
    setFilesList((await api.get(`/employees/${filesFor.id}/files`)).data);
  };

  const prettySize = (s) => !s ? "" : s < 1024 ? `${s} B` : s < 1024*1024 ? `${(s/1024).toFixed(1)} KB` : `${(s/(1024*1024)).toFixed(2)} MB`;
  const fileIcon = (m) => m?.startsWith("image/") ? <ImageIcon className="w-4 h-4 text-[#FF7A29]"/> : <FileText className="w-4 h-4 text-[#FF7A29]"/>;

  const companyName = (cid) => companies.find((c) => c.id === cid)?.name || cid?.slice(0, 8);
  const canManageLinks = user?.role === "superadmin";

  return (
    <div className="p-4 md:p-12" data-testid="employees-page">
      <div className="flex items-start justify-between mb-8 gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Risorse Umane</div>
          <h1 className="font-display text-4xl">Dipendenti</h1>
          <p className="text-[#555] mt-1 text-sm">Anagrafica completa · codice fiscale · multi-azienda</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {user?.role === "admin" && !user?.employee_id && (
            <Button
              onClick={async () => {
                if (!window.confirm("Vuoi essere registrato anche come dipendente di questa azienda? Potrai timbrare con il QR e usare il portale dipendente.")) return;
                try {
                  await api.post("/admin/promote-me-to-employee", { job: "Amministratore" });
                  toast.success("Sei stato aggiunto come dipendente. Effettua il logout e accedi nuovamente per vedere le opzioni dipendente.");
                  load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Errore");
                }
              }}
              data-testid="promote-me-employee-btn"
              variant="outline"
              className="rounded-full h-11 border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
            >
              <BadgeCheck className="w-4 h-4 mr-2"/> Aggiungimi come dipendente
            </Button>
          )}
          <Button
            onClick={() => { setPendingOpen(true); loadPending(); }}
            data-testid="link-employee-btn"
            variant="outline"
            className="rounded-full h-11 border-2 border-[#FF7A29] text-[#FF7A29] hover:bg-[#FFF0E3] relative"
          >
            <UserCheck className="w-4 h-4 mr-2"/> Collega dipendente
            {pending.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="pending-badge">
                {pending.length}
              </span>
            )}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-emp-btn" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] h-11 btn-glow">
                <Plus className="w-4 h-4 mr-2"/> Nuovo dipendente
              </Button>
            </DialogTrigger>
          <DialogContent className="rounded-3xl border-0 max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">Nuovo dipendente</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nome *</Label><Input data-testid="emp-name" required className="rounded-xl mt-1" value={form.name} onChange={up("name")}/></div>
              <div><Label>Cognome *</Label><Input data-testid="emp-lastname" required className="rounded-xl mt-1" value={form.lastname} onChange={up("lastname")}/></div>
              <div className="sm:col-span-2"><Label>Email *</Label><Input type="email" required className="rounded-xl mt-1" value={form.email} onChange={up("email")}/></div>
              <div className="sm:col-span-2"><Label>Codice Fiscale *</Label><Input data-testid="emp-cf" required className="rounded-xl mt-1 uppercase" value={form.fiscal_code} onChange={(e)=>setForm({...form, fiscal_code: e.target.value.toUpperCase()})}/></div>
              <div><Label>Mansione</Label><Input data-testid="emp-job" className="rounded-xl mt-1" value={form.job} onChange={up("job")} placeholder="Es. Impiegato"/></div>
              <div><Label>Reparto</Label><Input data-testid="emp-department" className="rounded-xl mt-1" value={form.department} onChange={up("department")} placeholder="Es. Amministrazione"/></div>
              <div><Label>Data di nascita</Label><Input type="date" className="rounded-xl mt-1" value={form.birth} onChange={up("birth")}/></div>
              <div><Label>Telefono</Label><Input className="rounded-xl mt-1" value={form.phone} onChange={up("phone")}/></div>
              <div className="sm:col-span-2"><Label>Indirizzo</Label><Input className="rounded-xl mt-1" value={form.address} onChange={up("address")}/></div>
              <div><Label>Città</Label><Input className="rounded-xl mt-1" value={form.city} onChange={up("city")}/></div>
              <div><Label>CAP</Label><Input className="rounded-xl mt-1" value={form.zip_code} onChange={up("zip_code")}/></div>
              <div className="sm:col-span-2">
                <Label>Commesse / Progetti assegnati</Label>
                {orders.length === 0 ? (
                  <div className="text-xs text-[#999] italic mt-2">Nessuna commessa disponibile. Creale dalla sezione <strong>Commesse</strong>.</div>
                ) : (
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="emp-orders-picker">
                    {orders.map((o) => {
                      const active = (form.order_ids || []).includes(o.id);
                      return (
                        <button
                          type="button"
                          key={o.id}
                          onClick={() => toggleFormOrder(o.id)}
                          data-testid={`emp-order-${o.id}`}
                          className={`text-xs px-3 py-1.5 rounded-full border-2 transition-colors ${active ? "bg-[#FF7A29] text-white border-[#FF7A29]" : "bg-white text-[#0A0A0A] border-[#EDE5D8] hover:border-[#FF7A29]"}`}
                        >
                          <span className="font-mono mr-1">{o.order_code}</span>
                          {o.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="emp-save" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00]">Salva</Button></DialogFooter>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filtro Supervisori */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Label className="text-xs uppercase tracking-widest text-[#757575] font-bold">Filtra</Label>
        <Select value={empFilter} onValueChange={setEmpFilter}>
          <SelectTrigger className="rounded-full w-64" data-testid="emp-role-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti ({items.length})</SelectItem>
            <SelectItem value="supervisor">Solo Supervisori ({items.filter(i => i.is_supervisor).length})</SelectItem>
            <SelectItem value="employee">Solo Dipendenti ({items.filter(i => !i.is_supervisor).length})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="card-smart overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-[#EDE5D8]">
            <tr className="text-left">
              <th className="p-4 font-semibold">Dipendente</th>
              <th className="p-4 font-semibold">Mansione</th>
              <th className="p-4 font-semibold">Codice Fiscale</th>
              <th className="p-4 font-semibold">Aziende</th>
              <th className="p-4 font-semibold">Commesse</th>
              <th className="p-4 font-semibold">Contatti</th>
              <th className="p-4 font-semibold text-center">Stato</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 && <tr><td colSpan="8" className="p-12 text-center text-[#757575]"><Users className="w-10 h-10 mx-auto mb-3 text-[#FF7A29]/50"/>Nessun dipendente per il filtro selezionato.</td></tr>}
            {filteredItems.map((it) => {
              const cids = it.company_ids || [it.company_id];
              const oids = it.order_ids && it.order_ids.length ? it.order_ids : (it.order_id ? [it.order_id] : []);
              return (
                <tr key={it.id} className="border-b border-[#EDE5D8] hover:bg-[#FFF0E3]/50 transition-colors" data-testid={`emp-${it.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={()=>setAvatarFor(it)}
                        title="Cambia avatar"
                        data-testid={`emp-avatar-${it.id}`}
                        className="rounded-full hover:ring-2 hover:ring-[#FF7A29] transition-all flex-shrink-0"
                      >
                        <Avatar avatar={it.avatar} name={it.name} lastname={it.lastname} size={36}/>
                      </button>
                      <div className="min-w-0">
                        <div className="font-semibold flex items-center gap-2 flex-wrap">
                          <span>{it.name} {it.lastname}</span>
                          {it.is_supervisor && (
                            <span data-testid={`supervisor-badge-${it.id}`} className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200">
                              <Eye className="w-2.5 h-2.5"/> Supervisore
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[#757575] truncate">{it.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">{it.job || "—"}</td>
                  <td className="p-4 font-mono text-xs">{it.fiscal_code}</td>
                  <td className="p-4">
                    <button
                      type="button"
                      onClick={() => setLinkFor(it)}
                      data-testid={`emp-companies-${it.id}`}
                      className="inline-flex items-center gap-1 text-xs bg-[#FFF0E3] text-[#E85D00] hover:bg-[#FF7A29] hover:text-white transition-colors px-2.5 py-1 rounded-full font-semibold"
                    >
                      <Building2 className="w-3 h-3"/> {cids.length} {cids.length === 1 ? "azienda" : "aziende"}
                    </button>
                  </td>
                  <td className="p-4">
                    <button
                      type="button"
                      onClick={() => openOrders(it)}
                      data-testid={`emp-orders-${it.id}`}
                      className="inline-flex items-center gap-1 text-xs bg-[#FFF0E3] text-[#E85D00] hover:bg-[#FF7A29] hover:text-white transition-colors px-2.5 py-1 rounded-full font-semibold"
                    >
                      <Briefcase className="w-3 h-3"/> {oids.length} {oids.length === 1 ? "commessa" : "commesse"}
                    </button>
                  </td>
                  <td className="p-4 text-xs text-[#757575]">
                    <div className="flex items-center gap-1"><Phone className="w-3 h-3"/> {it.phone || "—"}</div>
                  </td>
                  <td className="p-4 text-center" onClick={(e)=>e.stopPropagation()}>
                    <div className="inline-flex flex-col items-center gap-1" data-testid={`emp-active-${it.id}`}>
                      <button
                        type="button"
                        onClick={()=>toggleEmployeeActive(it)}
                        data-testid={`emp-switch-${it.id}`}
                        title={it.enabled === false ? "Clicca per riattivare" : "Clicca per disattivare"}
                        className={`relative inline-flex items-center h-7 w-14 rounded-full border-2 transition-colors focus:outline-none ${it.enabled === false ? "bg-red-500 border-red-600" : "bg-green-500 border-green-600"}`}
                      >
                        <span className={`inline-block w-5 h-5 rounded-full bg-white shadow-md transform transition-transform ${it.enabled === false ? "translate-x-0.5" : "translate-x-8"}`}/>
                        <span className={`absolute text-[9px] font-bold tracking-widest text-white ${it.enabled === false ? "right-1.5" : "left-1.5"}`}>
                          {it.enabled === false ? "OFF" : "ON"}
                        </span>
                      </button>
                      {it.enabled === false && it.disabled_reason && (
                        <span className="text-[9px] bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full max-w-[120px] truncate" title={it.disabled_reason} data-testid={`emp-reason-${it.id}`}>
                          {it.disabled_reason}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <Button size="icon" variant="ghost" onClick={()=>openFiles(it)} data-testid={`files-emp-${it.id}`} title="File del dipendente" className="hover:bg-[#FFF0E3] rounded-full h-8 w-8 mr-1">
                      <FolderOpen className="w-4 h-4"/>
                    </Button>
                    <Button size="icon" variant="ghost" onClick={()=>openAccount(it)} data-testid={`account-emp-${it.id}`} title="Account portale" className="hover:bg-[#FFF0E3] rounded-full h-8 w-8 mr-1">
                      <KeyRound className="w-4 h-4"/>
                    </Button>
                    {it.email && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={()=>resetEmployeePwd(it)}
                        data-testid={`reset-pwd-emp-${it.id}`}
                        title="Resetta password (admin override)"
                        className="rounded-full h-8 w-8 mr-1 hover:bg-blue-50 text-[#757575] hover:text-blue-700"
                      >
                        <RefreshCw className="w-4 h-4"/>
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={()=>toggleSupervisor(it)}
                      data-testid={`supervisor-toggle-${it.id}`}
                      title={it.is_supervisor ? "Rimuovi ruolo Supervisore (torna a dipendente)" : "Rendi Supervisore (accesso sola lettura)"}
                      className={`rounded-full h-8 w-8 mr-1 ${it.is_supervisor ? "bg-purple-100 text-purple-700 hover:bg-purple-200" : "hover:bg-purple-50 text-[#757575] hover:text-purple-700"}`}
                    >
                      {it.is_supervisor ? <Eye className="w-4 h-4"/> : <EyeOff className="w-4 h-4"/>}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={()=>del(it.id)} data-testid={`del-emp-${it.id}`} className="hover:bg-red-50 hover:text-red-600 rounded-full h-8 w-8">
                      <Trash2 className="w-4 h-4"/>
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Dialog gestione aziende associate */}
      <Dialog open={!!linkFor} onOpenChange={(v)=>{ if (!v) setLinkFor(null); }}>
        <DialogContent className="rounded-3xl border-0 max-w-lg" data-testid="emp-link-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Aziende associate</DialogTitle></DialogHeader>
          {linkFor && (
            <div className="space-y-4">
              <div className="text-sm">
                <div className="font-semibold">{linkFor.name} {linkFor.lastname}</div>
                <div className="text-xs text-[#757575]">{linkFor.email}</div>
              </div>
              <div className="space-y-2">
                {(linkFor.company_ids || [linkFor.company_id]).map((cid) => {
                  const isMain = cid === linkFor.company_id;
                  return (
                    <div key={cid} className="flex items-center justify-between bg-[#FFF0E3]/50 border border-[#EDE5D8] rounded-xl px-3 py-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 className="w-4 h-4 text-[#FF7A29]"/>
                        <span className="font-semibold">{companyName(cid)}</span>
                        {isMain && <span className="text-[10px] bg-[#0A0A0A] text-[#FF7A29] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">Principale</span>}
                      </div>
                      {!isMain && canManageLinks && (
                        <Button size="icon" variant="ghost" onClick={()=>removeCompanyLink(cid)} data-testid={`unlink-${cid}`} className="hover:bg-red-50 hover:text-red-600 rounded-full h-7 w-7">
                          <X className="w-3.5 h-3.5"/>
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
              {canManageLinks ? (
                <div className="pt-3 border-t border-[#EDE5D8] space-y-2">
                  <Label>Associa a nuova azienda</Label>
                  <div className="flex gap-2">
                    <Select value={addCid || undefined} onValueChange={setAddCid}>
                      <SelectTrigger data-testid="link-company-select" className="rounded-xl flex-1"><SelectValue placeholder="Seleziona azienda…"/></SelectTrigger>
                      <SelectContent>
                        {companies
                          .filter((c) => !(linkFor.company_ids || [linkFor.company_id]).includes(c.id))
                          .map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button onClick={addCompanyLink} disabled={!addCid} data-testid="link-add-btn" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00]">
                      <Plus className="w-4 h-4"/>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-[#757575] pt-2 border-t border-[#EDE5D8]">
                  Solo il Super Admin può associare dipendenti ad altre aziende.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Dialog gestione account portale */}
      <Dialog open={!!accountFor} onOpenChange={(v)=>{ if (!v) setAccountFor(null); }}>
        <DialogContent className="rounded-3xl border-0 max-w-md" data-testid="emp-account-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Accesso al portale</DialogTitle></DialogHeader>
          {accountFor && (
            <div className="space-y-4">
              <div className="text-sm bg-[#FFF0E3]/50 border border-[#EDE5D8] rounded-xl p-3">
                <div className="font-semibold">{accountFor.name} {accountFor.lastname}</div>
                <div className="text-xs text-[#757575]">
                  {accountInfo?.exists
                    ? <>✅ Account attivo · <strong>{accountInfo.email}</strong></>
                    : "Nessun accesso al portale configurato."}
                </div>
              </div>
              <div>
                <Label>Email portale</Label>
                <Input type="email" data-testid="acc-email" className="rounded-xl mt-1" value={newEmail} onChange={(e)=>setNewEmail(e.target.value)}/>
              </div>
              <div>
                <Label>{accountInfo?.exists ? "Nuova password" : "Password"}</Label>
                <Input type="password" data-testid="acc-pwd" className="rounded-xl mt-1" value={newPwd} onChange={(e)=>setNewPwd(e.target.value)} placeholder="Minimo 6 caratteri"/>
              </div>
              <div className="text-xs text-[#757575]">
                Il dipendente potrà accedere su <strong>/portal</strong> con queste credenziali per caricare foto e PDF.
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {accountInfo?.exists && (
                  <Button variant="ghost" onClick={revokeAccount} data-testid="acc-revoke" className="rounded-full text-red-600 hover:bg-red-50">
                    Revoca accesso
                  </Button>
                )}
                <Button onClick={saveAccount} data-testid="acc-save" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00]">
                  {accountInfo?.exists ? "Aggiorna password" : "Crea account"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog file dipendente (admin) */}
      <Dialog open={!!filesFor} onOpenChange={(v)=>{ if (!v) setFilesFor(null); }}>
        <DialogContent className="rounded-3xl border-0 max-w-2xl" data-testid="emp-files-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">File caricati dal dipendente</DialogTitle></DialogHeader>
          {filesFor && (
            <div className="space-y-4">
              <div className="text-sm">
                <div className="font-semibold">{filesFor.name} {filesFor.lastname}</div>
                <div className="text-xs text-[#757575]">{filesList.length} {filesList.length === 1 ? "file" : "file"} disponibili</div>
              </div>
              {filesList.length === 0 ? (
                <div className="text-center text-[#757575] py-12 bg-[#FFF0E3]/30 rounded-2xl">
                  <FolderOpen className="w-10 h-10 mx-auto mb-2 text-[#FF7A29]/50"/>
                  Il dipendente non ha ancora caricato file.
                </div>
              ) : (
                <div className="max-h-96 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-[#EDE5D8]"><tr className="text-left"><th className="p-2">Nome</th><th className="p-2">Categoria</th><th className="p-2">Dim.</th><th className="p-2">Data</th><th className="p-2"></th></tr></thead>
                    <tbody>
                      {filesList.map((f) => (
                        <tr key={f.id} className="border-b border-[#EDE5D8]" data-testid={`admin-file-${f.id}`}>
                          <td className="p-2">
                            <div className="flex items-center gap-2">
                              {fileIcon(f.mime_type)}
                              <div>
                                <div className="font-semibold text-xs">{f.filename}</div>
                                {f.notes && <div className="text-[10px] text-[#757575]">{f.notes}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="p-2 text-xs">
                            <span className="bg-[#FFF0E3] text-[#E85D00] px-2 py-0.5 rounded-full font-semibold uppercase text-[10px]">{f.category}</span>
                          </td>
                          <td className="p-2 text-xs text-[#757575]">{prettySize(f.size * 0.75)}</td>
                          <td className="p-2 text-xs text-[#757575]">{new Date(f.uploaded_at).toLocaleDateString("it-IT")}</td>
                          <td className="p-2 text-right">
                            <Button size="icon" variant="ghost" onClick={()=>downloadEmpFile(f)} data-testid={`admin-dl-${f.id}`} title="Scarica" className="hover:bg-[#FFF0E3] rounded-full h-7 w-7 mr-1">
                              <Download className="w-3.5 h-3.5"/>
                            </Button>
                            <Button size="icon" variant="ghost" onClick={()=>delEmpFile(f)} data-testid={`admin-del-file-${f.id}`} title="Elimina" className="hover:bg-red-50 hover:text-red-600 rounded-full h-7 w-7">
                              <Trash2 className="w-3.5 h-3.5"/>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Dialog modifica commesse rapide del dipendente */}
      <Dialog open={!!ordersFor} onOpenChange={(v)=>{ if (!v) setOrdersFor(null); }}>
        <DialogContent className="rounded-3xl border-0 max-w-lg" data-testid="emp-orders-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Commesse assegnate</DialogTitle></DialogHeader>
          {ordersFor && (
            <div className="space-y-4">
              <div className="text-sm bg-[#FFF0E3]/50 border border-[#EDE5D8] rounded-xl p-3">
                <div className="font-semibold">{ordersFor.name} {ordersFor.lastname}</div>
                <div className="text-xs text-[#757575]">{ordersFor.job || "—"} · {ordersFor.email}</div>
              </div>
              {orders.length === 0 ? (
                <div className="text-sm text-[#757575] text-center py-8">
                  <Briefcase className="w-8 h-8 mx-auto mb-2 text-[#FF7A29]/50"/>
                  Nessuna commessa disponibile. Creale nella sezione <strong>Commesse</strong>.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 max-h-72 overflow-auto">
                  {orders.map((o) => {
                    const active = ordersDraft.includes(o.id);
                    return (
                      <button
                        type="button"
                        key={o.id}
                        onClick={() => toggleDraftOrder(o.id)}
                        data-testid={`draft-order-${o.id}`}
                        className={`text-xs px-3 py-1.5 rounded-full border-2 transition-colors ${active ? "bg-[#FF7A29] text-white border-[#FF7A29]" : "bg-white text-[#0A0A0A] border-[#EDE5D8] hover:border-[#FF7A29]"}`}
                      >
                        <span className="font-mono mr-1">{o.order_code}</span>
                        {o.name}
                        {o.client_name && <span className="text-[10px] opacity-75"> · {o.client_name}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
              <DialogFooter>
                <Button onClick={saveOrders} data-testid="save-emp-orders-btn" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00]">
                  Salva commesse
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Dialog richieste di collegamento dipendente (auto-registrazioni pending) */}
      <Dialog open={pendingOpen} onOpenChange={setPendingOpen}>
        <DialogContent className="rounded-3xl border-0 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="emp-pending-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Richieste collegamento dipendenti</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-[#555]">
              Dipendenti che si sono registrati indicando la P.IVA della tua azienda. Approva per creare automaticamente l'utente del portale.
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#999]"/>
              <Input
                data-testid="pending-search"
                placeholder="Cerca per nome o email…"
                className="rounded-xl pl-9"
                value={pendingQuery}
                onChange={(e)=>setPendingQuery(e.target.value)}
              />
            </div>
            {pendingLoading ? (
              <div className="text-center text-[#757575] py-8">Caricamento…</div>
            ) : pending.length === 0 ? (
              <div className="text-center text-[#757575] py-12 bg-[#FFF0E3]/30 rounded-2xl" data-testid="pending-empty">
                <UserCheck className="w-10 h-10 mx-auto mb-2 text-[#FF7A29]/50"/>
                Nessuna richiesta in attesa.
                <div className="text-xs mt-2">Condividi il link <span className="font-mono bg-white px-2 py-0.5 rounded">/register-employee</span> con i tuoi dipendenti.</div>
              </div>
            ) : (
              <div className="space-y-2">
                {pending
                  .filter((p) => {
                    if (!pendingQuery) return true;
                    const q = pendingQuery.toLowerCase();
                    return (`${p.name} ${p.lastname}`.toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q));
                  })
                  .map((p) => (
                    <div key={p.id} className="border border-[#EDE5D8] rounded-xl p-4 bg-white" data-testid={`pending-${p.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                          <div className="font-semibold">{p.name} {p.lastname}</div>
                          <div className="text-xs text-[#555]">{p.email} {p.phone ? "· " + p.phone : ""}</div>
                          <div className="text-xs font-mono text-[#FF7A29] mt-1">CF: {p.fiscal_code || "—"}</div>
                          <div className="text-[10px] text-[#999] mt-1">Inviata {new Date(p.created_at).toLocaleString("it-IT")}</div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={()=>approvePending(p.id)}
                            data-testid={`approve-${p.id}`}
                            className="rounded-full bg-[#FF7A29] hover:bg-black text-white"
                          >
                            <Check className="w-3.5 h-3.5 mr-1"/> Approva
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={()=>rejectPending(p.id)}
                            data-testid={`reject-${p.id}`}
                            className="rounded-full border-red-300 text-red-600 hover:bg-red-50"
                          >
                            <XCircle className="w-3.5 h-3.5 mr-1"/> Rifiuta
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AvatarPicker
        open={!!avatarFor}
        onOpenChange={(v)=>{ if (!v) setAvatarFor(null); }}
        value={avatarFor?.avatar}
        name={avatarFor?.name || ""}
        lastname={avatarFor?.lastname || ""}
        onSelect={(newAv) => saveAvatarFor(avatarFor, newAv)}
      />

    </div>
  );
}
